import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { round } from '@u/utils';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap, take } from 'rxjs/operators';

import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RescuePanelService } from '@c/rescue-panel/rescue-panel.service';
import { IElementsVisibilitySymbolMenuConfig } from '@c/shared/symbol-menu/symbol-menu.model';
import { StrategiesNames } from '@c/top-menu/top-navigation-menu/top-navigation-menu.model';
import {
  TradingLogSymbolSummaryPanelItemValuesModel,
  TradingLogSymbolSummaryPanelStateType,
} from '@c/trading-log/shared';
import {
  ChartTabs,
  ExchangeCountriesCodes,
  Features,
  PinnedOrderPanelMinWidth,
  saveViewModeDebounceTime,
  TabletMinWidth,
  UserSettings,
  wheelCalcSectionMinHeight,
  wheelChartSectionMinHeight,
  wheelDataWindowMinHeight,
  wheelFilterDefaultHeight,
  wheelRightPartDefaultWidth,
  wheelRightPartMaxWidthPx,
  wheelRightPartMinWidth,
} from '@const';
import { WheelHeaderPopupComponent } from '@m1/common/wheel-header-popup/wheel-header-popup.component';
import { IWheelStateFromRedirect } from '@m1/wheel/wheel.model';
import { WheelService } from '@m1/wheel/wheel.service';
import { AIChatTriggerModel } from '@mod/ai-chats/pxo-ai-chat-triggers.model';
import { AppVersionBannerService } from '@s/app-version-banner.service';
import { DialogsService } from '@s/common';
import { ConcurrentLoginService } from '@s/concurrent-login.service';
import { EditionsService } from '@s/editions.service';
import { ObservableService as ObservableServiceV2 } from '@s/observable.service';
import { SymbolsService } from '@s/symbols.service';
import { UserDataService } from '@s/user-data.service';
import { StringifiedGuid } from '@t/common';
import { WheelViewModes } from '@t/wheel/wheel.types';
import { ObservableService } from 'src/app/core/directives/observable.service';
import { WheelCalculatorService } from './wheel-calculator/wheel-calculator.service';
import { WheelChartComponent } from './wheel-chart/wheel-chart.component';
import { WheelScannerService } from './wheel-scanner-tab/wheel-scanner.service';

@Component({
  selector: 'app-wheel',
  templateUrl: './wheel.component.html',
  styleUrls: ['./wheel.component.scss'],
  standalone: false,
})
export class WheelComponent implements OnInit, AfterViewInit, OnDestroy {
  // Constants for layout calculations
  private readonly minLeftSpacePx = 50;
  private readonly maxAllowablePercent = 90;
  private readonly precisionDigits = 6;
  private readonly pinnedPanelWidthPx = 370;
  private readonly gutterSizePx = 6;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected selectedSymbol: any[];
  protected premiumsValue = true;
  protected isBanner = true;
  protected showMaintenance = false;
  protected isWheelScannerLoadingFirstTime = true;
  protected readonly showMaintenance$ = this.observableServiceV2.showMaintenance;

  protected selectedTab: string = ChartTabs.Chart;
  protected chartTabs = ChartTabs;
  protected isSymbolMenuOpened = false;
  protected wheelTabsVisibilityConfig: Partial<IElementsVisibilitySymbolMenuConfig> = {
    showCalculatorTab: true,
    showReportTab: false,
    showRescueTab: false,
    showOptChainTab: true,
    showRollTab: true,
  };
  protected wheelSplitModeTabsVisibilityConfig: Partial<IElementsVisibilitySymbolMenuConfig> = {
    showCalculatorTab: false,
    showReportTab: false,
    showRescueTab: false,
    showOptChainTab: true,
    showRollTab: true,
  };

  protected viewMode$ = this.observableServiceV2.wheelViewMode;
  protected readonly viewModes = WheelViewModes;

  private desktopRightContentWidthPercent: number | null = null;
  private subscriptions = new Subscription();
  protected isMobile = false;
  private width: number = window.innerWidth;
  private height: number = window.innerHeight;
  private rightPartWidthInPercents = 35;
  private rightPartMinWidthInPercents = 25;
  private rightPartMaxWidthInPercents = 60;
  private leftPartWidthInPercents = 65;
  private isFirstPinnedPanelLayout = true;
  protected wheelChartSectionHeightInPercents: number;
  protected wheelCalcSectionHeightInPercents: number;
  protected wheelChartSectionMinHeightInPercents: number;
  protected wheelCalcSectionMinHeightInPercents: number;
  protected dataWindowMinHeightInPercents: number;
  protected wheelFilterHeight: number;
  protected wheelDataWindowHeight: number;

  public isShowChartOverlay = false;
  public mobileWidth = TabletMinWidth;
  public strategiesNames = StrategiesNames;

  protected currentSymbol$ = this.observableServiceV2.wheelSymbol;
  protected features = Features;
  protected showOrderPanel = false;
  protected showRescuePanel = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected userSetting: any = {};
  protected user = { first_name: '', last_name: 'undefined', theme: 1, show_mm_trades: 0, access_type: 1 };
  protected readonly wheelViewMode$ = this.observableServiceV2.wheelViewMode;
  protected readonly wheelViewModes = WheelViewModes;
  protected tradingLogSymbolSummaryPanelSelectedGroupId: StringifiedGuid = null;

  private readonly wheelViewModeState$ = new Subject<WheelViewModes>();
  private readonly wheelTabState$ = new Subject<string>();

  protected tradingLogSymbolSummaryPanelItemValues: TradingLogSymbolSummaryPanelItemValuesModel = {};

  protected readonly currentSymbolDetails$ = this.currentSymbol$.pipe(
    switchMap((securityId) => this.symbolsService.getById(securityId)),
  );

  protected readonly currentSymbolDetails = toSignal(this.currentSymbolDetails$);
  protected readonly analyzeTriggerDetails = computed<AIChatTriggerModel>(() => {
    const currentSymbolDetails = this.currentSymbolDetails();

    if (!currentSymbolDetails) {
      return null;
    }

    return {
      name: 'technical_chart_analysis',
      options: {
        strategy: 'Wheel',
        ticker: currentSymbolDetails.symbol,
        country_code: currentSymbolDetails.country_code,
      },
    };
  });

  @ViewChild(WheelChartComponent) chart: WheelChartComponent;

  constructor(
    private dialogsService: DialogsService,
    private scannerService: WheelScannerService,
    public observableService: ObservableService,
    protected observableServiceV2: ObservableServiceV2,
    private wheelCalculatorService: WheelCalculatorService,
    private userDataService: UserDataService,
    private appVersionBannerService: AppVersionBannerService,
    private concurrentLoginService: ConcurrentLoginService,
    private symbolsService: SymbolsService,
    private wheelService: WheelService,
    private location: Location,
    private changeDetectorRef: ChangeDetectorRef,
    private editionsService: EditionsService,
    private dialog: MatDialog,
    private rescuePanelService: RescuePanelService,
  ) {}

  @HostListener('window:resize', ['$event']) onResize(event): void {
    this.onWindowResize(event);
    this.checkSymbolTabForDesktop();
  }

  async ngOnInit(): Promise<void> {
    // Enable the rescue panel button on the wheel page
    this.rescuePanelService.closePanel();
    this.rescuePanelService.setShowButton(true);
    this.showRescuePanel = false;

    // Initialize mobile view and rescue tab visibility
    this.isMobile = this.width <= this.mobileWidth;
    this.wheelTabsVisibilityConfig = {
      ...this.wheelTabsVisibilityConfig,
      showRescueTab: this.isMobile,
    };

    this.showMaintenance = this.observableServiceV2.showMaintenance.getValue();
    this.handleShowOrderPanel();
    this.handleShowRescuePanel();

    const stateFromRedirect: IWheelStateFromRedirect = this.location.getState() as IWheelStateFromRedirect;

    if (stateFromRedirect && stateFromRedirect.symbol) {
      const symbol = await this.symbolsService.getBySymbol(stateFromRedirect.symbol.symbol, ExchangeCountriesCodes.US);

      if (!symbol) {
        return;
      }

      await this.wheelService.setSymbolForWheelPage(stateFromRedirect.symbol);
    }

    await this.restoreDesktopRightWidth();

    this.subscriptions.add(
      this.observableServiceV2.wheelFiltersHeight.pipe(take(1)).subscribe((filtersHeight) => {
        this.wheelFilterHeight = filtersHeight;
      }),
    );
    this.subscriptions.add(
      this.observableServiceV2.showMaintenance.subscribe((showMaintenance) => (this.showMaintenance = showMaintenance)),
    );
    this.calculateWidthForSplitAreas();
    this.subscriptions.add(
      this.observableService.isBanner.subscribe((res) => {
        this.isBanner = res;
      }),
    );

    if (this.getCookie('isBanner') == '') {
      this.isBanner = false;
    } else if (this.getCookie('isBanner') != '' && this.getCookie('isBanner') == 'true') {
      if (this.getCookie('bannerStatus') != '' && this.getCookie('bannerStatus') == '1') {
        this.isBanner = true;
      } else {
        this.isBanner = false;
      }
    } else if (this.getCookie('isBanner') != '' && this.getCookie('isBanner') == 'false') {
      this.isBanner = false;
    }

    this.subscriptions.add(
      this.observableServiceV2.WheelTab.subscribe((tab) => {
        this.selectedTab = tab;
      }),
    );
    const { collapsed } = this.observableServiceV2.WheelSymbolMenuOptions.getValue();
    this.isSymbolMenuOpened = collapsed;
    this.premiumsValue = this.observableServiceV2.isWheelPremiums.getValue();

    this.subscriptions.add(
      this.observableServiceV2.wheelChartSectionHeight.pipe(take(1)).subscribe((wheelChartHeight) => {
        this.wheelChartSectionHeightInPercents = wheelChartHeight;
        this.wheelCalcSectionHeightInPercents = 100 - this.wheelChartSectionHeightInPercents;
      }),
    );

    this.subscriptions.add(
      this.observableServiceV2.isWheelPremiums.subscribe((isWheelPremiums) => (this.premiumsValue = isWheelPremiums)),
    );

    this.subscriptions.add(
      this.observableServiceV2.tradingPanelOrderModalPinned.subscribe(() => this.handleShowOrderPanel()),
    );

    this.subscriptions.add(
      this.observableServiceV2.tradingPanelOrderInput.subscribe(() => this.handleShowOrderPanel()),
    );

    this.subscriptions.add(
      this.observableService.userSetting.subscribe((userSetting) => {
        this.userSetting = userSetting;
      }),
    );

    // from header V1 for wheel
    this.subscriptions.add(
      this.wheelCalculatorService.updateUserSetting$.subscribe((res) => {
        if (res) {
          this.userSetting = res;
        }
      }),
    );

    // from header V1 for wheel
    if (!Object.keys(this.userSetting).length) {
      this.userSetting = JSON.parse(localStorage.getItem('user'));
    }

    this.wheelViewModeState$.pipe(debounceTime(saveViewModeDebounceTime)).subscribe(async (viewMode) => {
      this.observableServiceV2.wheelViewMode.next(viewMode);
      await this.userDataService.set(UserSettings.WheelViewMode, viewMode);
    });

    this.wheelTabState$.pipe(debounceTime(saveViewModeDebounceTime)).subscribe(async (tab) => {
      this.observableServiceV2.WheelTab.next(tab);
      await this.userDataService.set(UserSettings.WheelTab, tab);
    });

    this.appVersionBannerService.subscribe();
    this.concurrentLoginService.subscribe();
  }

  ngAfterViewInit(): void {
    this.calculateWidthForSplitAreas();

    this.subscriptions.add(
      this.observableServiceV2.isWheelScannerLoadingFirstTime$
        .pipe(take(1))
        .subscribe((isWheelScannerLoadingFirstTime) => {
          this.isWheelScannerLoadingFirstTime = isWheelScannerLoadingFirstTime;

          // trigger resize for chart container to recalculate chart size inside it
          setTimeout(() => {
            this.calculateDesktopLayout();
            this.changeDetectorRef.markForCheck();
          }, 200);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.appVersionBannerService.unsubscribe();
    this.concurrentLoginService.unsubscribe();
    this.rescuePanelService.setShowButton(false);
  }

  public onDragStart(): void {
    this.isShowChartOverlay = true;
  }

  public onDragEnd(): void {
    this.isShowChartOverlay = false;
  }

  async onSetCurrentSymbol(event): Promise<void> {
    if (event) {
      this.selectedSymbol = event;

      if (this.selectedSymbol.length > 0) {
        this.scannerService.setSymbolChart$(this.selectedSymbol);
      } else {
        this.selectedSymbol = null;
      }
    }
  }

  getCookie(cname): string {
    const name = cname + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  async onTabChanged(tab: string): Promise<void> {
    this.selectedTab = tab;
    this.wheelTabState$.next(tab);

    // Handle rescue tab state
    if (tab === ChartTabs.Rescue) {
      this.rescuePanelService.openPanel();
    } else if (this.rescuePanelService.isPanelOpen()) {
      this.rescuePanelService.closePanel();
    }
  }

  async onToggleSubTabbing(value): Promise<void> {
    await this.userDataService.set(UserSettings.IsWheelPremiums, value);
  }

  onWindowResize(event): void {
    this.width = event.target.innerWidth;
    this.height = event.target.innerHeight;
    const wasMobile = this.isMobile;
    this.isMobile = this.width <= this.mobileWidth;

    // Handle mode change from mobile to desktop
    if (wasMobile && !this.isMobile) {
      if (this.selectedTab === ChartTabs.Rescue) {
        this.selectedTab = ChartTabs.Chart;
        this.wheelTabState$.next(this.selectedTab);
        this.rescuePanelService.openPanel();
      }
    }

    // Handle mode change from desktop to mobile
    if (!wasMobile && this.isMobile) {
      if (this.rescuePanelService.isPanelOpen()) {
        this.rescuePanelService.closePanel();
        this.selectedTab = ChartTabs.Rescue;
        this.wheelTabState$.next(this.selectedTab);
      }
    }

    // Update rescue tab visibility based on mobile view
    if (wasMobile !== this.isMobile) {
      this.wheelTabsVisibilityConfig = {
        ...this.wheelTabsVisibilityConfig,
        showRescueTab: this.isMobile,
      };
    }

    if (!this.isMobile) {
      this.calculateDesktopLayout();
    }

    this.changeDetectorRef.markForCheck();
    this.handlePinnedOrderModal();
  }

  private async restoreDesktopRightWidth(): Promise<void> {
    const storedFromObservable = this.tryParseNumber(this.observableServiceV2.wheelFiltersWidth.getValue());

    if (this.isFiniteNumber(storedFromObservable) && storedFromObservable > 0) {
      this.desktopRightContentWidthPercent = storedFromObservable;
      return;
    }

    const storedFromServer = await this.userDataService.get(UserSettings.WheelFiltersWidth);
    const parsedServerValue = this.tryParseNumber(storedFromServer);

    if (this.isFiniteNumber(parsedServerValue) && parsedServerValue > 0) {
      this.desktopRightContentWidthPercent = parsedServerValue;
      this.observableServiceV2.wheelFiltersWidth.next(parsedServerValue);
    } else {
      this.desktopRightContentWidthPercent = null;
    }
  }

  private tryParseNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  private checkSymbolTabForDesktop(): void {
    const viewMod = this.wheelViewMode$.getValue();

    if (
      !this.isMobile &&
      (this.selectedTab === ChartTabs.Signal ||
        this.selectedTab === ChartTabs.Rescue ||
        (viewMod === this.wheelViewModes.Split && this.selectedTab === ChartTabs.Calculator))
    ) {
      this.onTabChanged(ChartTabs.Chart);
    }
  }

  calculateWidthForSplitAreas(): void {
    if (this.isMobile) {
      return;
    }

    this.calculateDesktopLayout();
  }

  private calculateDesktopLayout(): void {
    if (this.isMobile) {
      return;
    }

    const widthCalc = this.getWidthCalculationConstants();
    const pinnedPanelWidthPx = this.getPinnedPanelWidthPx();
    const pinnedPanelWidthPercent = this.roundToPrecision(pinnedPanelWidthPx / widthCalc.onePercent);
    const { min, max } = this.getContentWidthBounds(widthCalc, pinnedPanelWidthPx, pinnedPanelWidthPercent);
    const defaultWidthPercent = this.roundToPrecision(wheelRightPartDefaultWidth / widthCalc.onePercent);
    const targetWidth = this.resolveContentWidth(min, max, defaultWidthPercent);

    const actualMinWidth = this.roundToPrecision(
      Math.min(min + pinnedPanelWidthPercent, widthCalc.maxAllowablePercent),
    );
    const actualMaxWidth = this.roundToPrecision(
      Math.min(max + pinnedPanelWidthPercent, widthCalc.maxAllowablePercent),
    );

    this.rightPartMinWidthInPercents = Math.min(actualMinWidth, actualMaxWidth);
    this.rightPartMaxWidthInPercents = Math.max(actualMinWidth, actualMaxWidth);

    const desiredRightWidth = this.roundToPrecision(targetWidth + pinnedPanelWidthPercent);
    this.rightPartWidthInPercents = this.clampWidth(
      desiredRightWidth,
      this.rightPartMinWidthInPercents,
      this.rightPartMaxWidthInPercents,
    );
    this.leftPartWidthInPercents = this.roundToPrecision(100 - this.rightPartWidthInPercents);

    this.calculateHeightValues();
  }

  private getWidthCalculationConstants(): {
    availableWidth: number;
    onePercent: number;
    minLeftSpacePx: number;
    maxAllowablePercent: number;
  } {
    const availableWidth = this.width - this.gutterSizePx;
    return {
      availableWidth,
      onePercent: availableWidth / 100,
      minLeftSpacePx: this.minLeftSpacePx,
      maxAllowablePercent: this.maxAllowablePercent,
    };
  }

  private getPinnedPanelWidthPx(): number {
    return this.showOrderPanel || this.showRescuePanel ? this.pinnedPanelWidthPx : 0;
  }

  private getContentWidthBounds(
    widthCalc: ReturnType<typeof this.getWidthCalculationConstants>,
    pinnedWidthPx: number,
    pinnedWidthPercent: number,
  ): { min: number; max: number } {
    const minPercent = this.roundToPrecision(wheelRightPartMinWidth / widthCalc.onePercent);
    const maxByConfigPercent = this.roundToPrecision(wheelRightPartMaxWidthPx / widthCalc.onePercent);
    const availablePx = widthCalc.availableWidth - pinnedWidthPx - widthCalc.minLeftSpacePx;
    const maxByAvailablePercent = this.roundToPrecision(availablePx / widthCalc.onePercent);
    const maxAllowedByPercent = widthCalc.maxAllowablePercent - pinnedWidthPercent;

    let maxPercent = Math.min(maxByConfigPercent, maxByAvailablePercent, maxAllowedByPercent);

    if (!Number.isFinite(maxPercent) || maxPercent < minPercent) {
      maxPercent = minPercent;
    }

    return { min: minPercent, max: maxPercent };
  }

  private resolveContentWidth(min: number, max: number, fallback: number): number {
    const preferredWidth = this.ensurePreferredContentWidth(fallback);

    return this.clampWidth(preferredWidth, min, max);
  }

  private ensurePreferredContentWidth(fallback: number): number {
    if (!this.isFiniteNumber(this.desktopRightContentWidthPercent) || this.desktopRightContentWidthPercent <= 0) {
      this.desktopRightContentWidthPercent = this.roundToPrecision(fallback);
    }

    return this.desktopRightContentWidthPercent;
  }

  private clampWidth(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return this.roundToPrecision(min);
    }

    if (max < min) {
      max = min;
    }

    return this.roundToPrecision(Math.min(Math.max(value, min), max));
  }

  private isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private roundToPrecision(value: number, precision: number = this.precisionDigits): number {
    return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  }

  private calculateHeightValues(): void {
    const onePercentFromCurrentHeight = this.height / 100;
    const onePercentFromAvailableWidth = (this.width - this.gutterSizePx) / 100;

    this.wheelChartSectionMinHeightInPercents = wheelChartSectionMinHeight / onePercentFromAvailableWidth;
    this.wheelCalcSectionMinHeightInPercents = wheelCalcSectionMinHeight / onePercentFromAvailableWidth;

    this.dataWindowMinHeightInPercents = wheelDataWindowMinHeight / onePercentFromCurrentHeight;
    this.wheelFilterHeight = this.wheelFilterHeight || wheelFilterDefaultHeight;
    this.wheelDataWindowHeight = 100 - this.wheelFilterHeight;
  }

  public getLeftPartWidth(): number {
    return this.leftPartWidthInPercents;
  }

  public getRightPartWidth(): number {
    return this.rightPartWidthInPercents;
  }

  public getRightPartMinWidth(): number {
    return this.rightPartMinWidthInPercents;
  }

  public getRightPartMaxWidth(): number {
    return this.rightPartMaxWidthInPercents;
  }

  async saveWheelFiltersWidth({ sizes }): Promise<void> {
    if (this.isMobile) {
      return;
    }

    const widthCalc = this.getWidthCalculationConstants();
    const pinnedWidthPx = this.getPinnedPanelWidthPx();
    const pinnedWidthPercent = this.roundToPrecision(pinnedWidthPx / widthCalc.onePercent);
    const { min, max } = this.getContentWidthBounds(widthCalc, pinnedWidthPx, pinnedWidthPercent);

    const actualRightSize = Number(sizes[1]);
    const contentWidth = this.clampWidth(actualRightSize - pinnedWidthPercent, min, max);

    this.desktopRightContentWidthPercent = contentWidth;
    this.calculateDesktopLayout();

    this.observableServiceV2.wheelFiltersWidth.next(contentWidth);
    await this.userDataService.set(UserSettings.WheelFiltersWidth, contentWidth);
  }

  async saveWheelChartHeight({ sizes }): Promise<void> {
    const chartSectionHeight = round(sizes[0], 2);
    await this.userDataService.set(UserSettings.WheelChartSectionHeight, chartSectionHeight);
  }

  async saveWheelFilterHeight({ sizes }): Promise<void> {
    const wheelFilterHeight = round(sizes[0], 2);
    await this.userDataService.set(UserSettings.WheelFiltersHeight, wheelFilterHeight);
  }

  private recalcSplit(afterPanelToggle = false): void {
    if (this.isMobile) {
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.calculateDesktopLayout();

    if (afterPanelToggle && this.isFirstPinnedPanelLayout) {
      this.isFirstPinnedPanelLayout = false;
      requestAnimationFrame(() => {
        this.calculateDesktopLayout();
        this.changeDetectorRef.markForCheck();
      });
      return;
    }

    this.changeDetectorRef.markForCheck();
  }

  private handleShowOrderPanel(): void {
    const isPinned = this.observableServiceV2.tradingPanelOrderModalPinned.getValue();
    const orderInput = this.observableServiceV2.tradingPanelOrderInput.getValue();
    const shouldShowOrderPanel = isPinned && !!orderInput;

    if (shouldShowOrderPanel === this.showOrderPanel) {
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.showOrderPanel = shouldShowOrderPanel;

    if (this.showOrderPanel && this.rescuePanelService.isPanelOpen()) {
      this.rescuePanelService.closePanel();
    }

    this.recalcSplit(true);
  }

  private handlePinnedOrderModal(): void {
    if (!this.showOrderPanel || this.width > PinnedOrderPanelMinWidth) {
      return;
    }

    this.observableServiceV2.tradingPanelOrderModalPinned.next(false);

    const orderInput = this.observableServiceV2.tradingPanelOrderInput.getValue();
    this.dialogsService.openTradingPanelOrderDialog(orderInput);
  }

  private handleShowRescuePanel(): void {
    this.subscriptions.add(
      this.rescuePanelService.isOpen$.subscribe((isOpen) => {
        if (isOpen === this.showRescuePanel) {
          this.changeDetectorRef.markForCheck();
          return;
        }

        this.showRescuePanel = isOpen;

        if (this.showRescuePanel && this.showOrderPanel) {
          this.showOrderPanel = false;
        }

        this.recalcSplit(true);
      }),
    );
  }

  protected wheelPositionSizing(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['position-sizing', 'buying-power', 'modals'];
    dialogConfig.disableClose = true;
    this.dialog.open(WheelHeaderPopupComponent, dialogConfig);
    document.getElementsByClassName('modals')[0].scrollTo(0, 0);
  }

  protected summaryPanelValueSelected(value: TradingLogSymbolSummaryPanelItemValuesModel): void {
    this.scannerService.currentSummaryPanelValue$.next(value);

    this.tradingLogSymbolSummaryPanelItemValues = {
      ...value,
    };

    if (this.chart) {
      this.chart.tradingLogSymbolSummaryPanelItemValuesModel = value;
      this.chart.addSummaryPanelLines();
    }

    this.tradingLogSymbolSummaryPanelSelectedGroupId = value?.groupId ?? null;
  }

  protected onLinesChange(values: Record<string, number | null>): void {
    if (this.chart) {
      this.chart.rescueLinesData = values;
      this.chart.addRescueLines();
    }
  }

  protected redirectToLowHighIndicatorDemoPage(): void {
    this.editionsService.redirectToDemoPage(Features.LowHighIndicator);
  }

  protected async summaryPanelStateUpdates(value: TradingLogSymbolSummaryPanelStateType): Promise<void> {
    await this.userDataService.set(UserSettings.TradingLogSymbolSummaryPanelWheelState, value);
  }

  protected changeViewMode(viewMode: WheelViewModes): void {
    this.selectedTab = this.chartTabs.Chart;
    this.wheelTabState$.next(this.selectedTab);
    this.wheelViewModeState$.next(viewMode);
    this.rescuePanelService.closePanel();
  }

  protected onSymbolMenuToggle(): void {
    this.isSymbolMenuOpened = !this.isSymbolMenuOpened;
    const symbolMenuOptions = { collapsed: this.isSymbolMenuOpened };
    this.observableServiceV2.WheelSymbolMenuOptions.next(symbolMenuOptions);
    this.userDataService.set(UserSettings.WheelSymbolMenuOptions, symbolMenuOptions);
  }

  protected changeSecurityId(securityId: number): void {
    this.currentSymbol$.next(securityId);
    this.userDataService.set(UserSettings.WheelSymbol, securityId);
  }

  protected changeGroupId(groupId: StringifiedGuid | null): void {
    this.tradingLogSymbolSummaryPanelSelectedGroupId = groupId;
  }
}
